import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Shield, Plus, Edit2, Trash2, Save, Lock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatError = (error) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(', ');
  if (detail && typeof detail === 'object') return detail.msg || JSON.stringify(detail);
  return error?.message || 'Error';
};

const emptyForm = { key: '', name: '', description: '', color: '#6A736B', permissions: [] };

export function Roles() {
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [rolesRes, catalogRes] = await Promise.all([
        axios.get(`${API_URL}/api/roles`, { withCredentials: true }),
        axios.get(`${API_URL}/api/roles/permissions-catalog`, { withCredentials: true }),
      ]);
      setRoles(rolesRes.data);
      setCatalog(catalogRes.data);
    } catch (e) {
      console.error('Error loading roles', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const categorized = useMemo(() => {
    const groups = {};
    catalog.forEach((p) => {
      groups[p.category] = groups[p.category] || [];
      groups[p.category].push(p);
    });
    return groups;
  }, [catalog]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setError('');
    setDialogOpen(true);
  };

  const openEdit = (role) => {
    setEditing(role);
    setForm({
      key: role.key,
      name: role.name || '',
      description: role.description || '',
      color: role.color || '#6A736B',
      permissions: role.permissions || [],
    });
    setError('');
    setDialogOpen(true);
  };

  const togglePerm = (permKey) => {
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(permKey)
        ? f.permissions.filter((p) => p !== permKey)
        : [...f.permissions, permKey],
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      if (editing) {
        // Only send editable fields; do not resend key on update
        const payload = {
          name: form.name,
          description: form.description,
          color: form.color,
        };
        if (editing.key !== 'admin') payload.permissions = form.permissions;
        await axios.put(`${API_URL}/api/roles/${editing.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/roles`, form, { withCredentials: true });
      }
      setDialogOpen(false);
      fetchAll();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (role) => {
    if (!window.confirm(`¿Eliminar el rol "${role.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/roles/${role.id}`, { withCredentials: true });
      fetchAll();
    } catch (e) {
      alert(formatError(e));
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-[1200px]" data-testid="roles-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1A1C18] tracking-tight flex items-center gap-2">
            <Shield size={24} className="text-[#2B4C3B] sm:w-7 sm:h-7" />
            Roles y Permisos
          </h1>
          <p className="text-[#6A736B] text-sm mt-1">
            Define qué puede hacer cada tipo de usuario en el sistema.
          </p>
        </div>
        <Button
          data-testid="new-role-btn"
          onClick={openCreate}
          className="bg-[#2B4C3B] hover:bg-[#1F3A2C] text-white w-full sm:w-auto"
        >
          <Plus size={16} className="mr-2" /> Nuevo Rol
        </Button>
      </div>

      {loading ? (
        <div className="text-center text-[#6A736B] py-12">Cargando…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {roles.map((role) => (
            <Card key={role.id} className="bg-white border-[#E8E5DD] rounded-xl" data-testid={`role-card-${role.key}`}>
              <CardHeader className="pb-2 flex flex-row items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: role.color }} />
                  <CardTitle className="text-base font-semibold text-[#1A1C18]">
                    {role.name}
                  </CardTitle>
                  {role.is_system && (
                    <span title="Rol del sistema" className="inline-flex items-center text-[10px] text-[#6A736B] bg-[#F3EBE1] rounded-full px-2 py-0.5">
                      <Lock size={10} className="mr-1" /> Sistema
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    data-testid={`edit-role-${role.key}`}
                    variant="ghost" size="sm"
                    onClick={() => openEdit(role)}
                    className="text-[#2B4C3B] hover:bg-[#F3EBE1] h-8 w-8 p-0"
                  >
                    <Edit2 size={14} />
                  </Button>
                  {!role.is_system && (
                    <Button
                      data-testid={`delete-role-${role.key}`}
                      variant="ghost" size="sm"
                      onClick={() => handleDelete(role)}
                      className="text-[#A65C5C] hover:bg-red-50 h-8 w-8 p-0"
                    >
                      <Trash2 size={14} />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-[#6A736B] mb-3 min-h-[32px]">{role.description || '—'}</p>
                <div className="text-[11px] text-[#1A1C18]">
                  <span className="font-medium">{(role.permissions || []).length}</span> permisos asignados
                </div>
                <p className="text-[10px] text-[#6A736B] mt-1 font-mono truncate">{role.key}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-[#E8E5DD] max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-[#1A1C18]">
              {editing ? `Editar rol: ${editing.name}` : 'Nuevo rol'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {!editing && (
              <div>
                <Label>Key (identificador único, minúsculas)</Label>
                <Input
                  data-testid="role-key-input"
                  value={form.key}
                  onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                  placeholder="ej: anestesista"
                  className="mt-1 font-mono"
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 sm:col-span-1">
                <Label>Nombre</Label>
                <Input
                  data-testid="role-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="ej: Anestesista"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Color</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="color"
                    data-testid="role-color-input"
                    value={form.color}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer border border-[#E8E5DD]"
                  />
                  <Input
                    value={form.color}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                    className="font-mono text-xs"
                  />
                </div>
              </div>
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea
                data-testid="role-description-input"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Descripción breve del rol"
                className="mt-1"
                rows={2}
              />
            </div>

            <div>
              <Label className="mb-2 block">
                Permisos {editing?.key === 'admin' && (
                  <span className="text-xs font-normal text-[#6A736B] ml-2">(no editable — Administrador siempre tiene todo)</span>
                )}
              </Label>
              <div className="space-y-3 border border-[#E8E5DD] rounded-lg p-3 max-h-[300px] overflow-y-auto">
                {Object.entries(categorized).map(([category, perms]) => (
                  <div key={category}>
                    <div className="text-xs font-semibold text-[#2B4C3B] uppercase tracking-wider mb-1.5">
                      {category}
                    </div>
                    <div className="space-y-1.5 pl-1">
                      {perms.map((p) => (
                        <label
                          key={p.key}
                          className="flex items-start gap-2 text-sm text-[#1A1C18] cursor-pointer hover:bg-[#F9F7F2] px-2 py-1 rounded"
                        >
                          <input
                            type="checkbox"
                            data-testid={`perm-${p.key}`}
                            disabled={editing?.key === 'admin'}
                            checked={form.permissions.includes(p.key)}
                            onChange={() => togglePerm(p.key)}
                            className="mt-1 h-4 w-4 accent-[#2B4C3B]"
                          />
                          <div className="flex-1">
                            <div>{p.label}</div>
                            <div className="text-[10px] text-[#6A736B] font-mono">{p.key}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
                Cancelar
              </Button>
              <Button
                data-testid="save-role-btn"
                onClick={handleSave}
                disabled={saving || !form.name || (!editing && !form.key)}
                className="bg-[#2B4C3B] hover:bg-[#1F3A2C] text-white"
              >
                <Save size={16} className="mr-2" />
                {saving ? 'Guardando…' : (editing ? 'Actualizar' : 'Crear rol')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Roles;
